/**
 * Working out how this page load is authenticated.
 *
 * Kept as a plain async function rather than living inside the provider,
 * because this is the part with the history. The rule it encodes:
 *
 *   A `?token=` in the URL is a magic link being redeemed for the first time.
 *   Its ABSENCE is not signed-out - consumeTokenMiddleware answers that request
 *   with a session cookie and a 302 to the same path without the token, so the
 *   very next load has no token and a perfectly good cookie. Ask the server
 *   before concluding anything.
 *
 * The old page got this right in `init()` and wrong in `apiRaw()`, which is how
 * "Confirm & send" came to answer "Invalid or expired token" to every
 * magic-link user. One place, one rule, one test.
 *
 * @module web/state/session
 */
import { MembershipRequiredError, SessionExpiredError, get, setAuthToken } from "../api/client.ts";
import type { BrandResponse } from "../api/types.ts";

export type AuthStatus = "loading" | "authenticated" | "signed-out" | "membership-required" | "error";

export interface Session {
  status: AuthStatus;
  brand: BrandResponse | null;
  /** Set only when `status` is "error" - a fault, not a refusal. */
  error: string | null;
}

/**
 * Resolve the session for this page load.
 *
 * @param search `location.search` - the query string, token included if present.
 */
export async function bootstrapSession(search: string): Promise<Session> {
  const token = new URLSearchParams(search).get("token");
  if (token) setAuthToken(token);

  try {
    const brand = await get<BrandResponse>("/api/brand");
    return { status: "authenticated", brand, error: null };
  } catch (e) {
    if (e instanceof MembershipRequiredError) {
      return { status: "membership-required", brand: null, error: null };
    }
    // A refused session is the ordinary signed-out case: show sign-in.
    if (e instanceof SessionExpiredError) {
      setAuthToken(null);
      return { status: "signed-out", brand: null, error: null };
    }
    // Anything else - a 500, the network - is a fault. Saying "please sign in"
    // to someone who IS signed in sends them to fetch a link that will not help.
    return {
      status: "error",
      brand: null,
      error: e instanceof Error ? e.message : "Could not reach the portal.",
    };
  }
}
